import { useMemo } from "react";
import { ActionMenu } from "components/ui";
import {
  LANE_WINDOW,
  PERIOD_STATUS_ARCHIVED,
  chainOf,
  formatDay,
  hasRange,
  groupedLocksFor,
  isDefaultPeriod,
  moduleOf,
  openPeriodLimitReplacementsFor,
  previousOf,
} from "helpers/periods";
import { isSameModule, moduleToSortValue, moduleToFallback } from "helpers/module";
import { usePeriodStrings } from "translation/periodManagement";

const LOCKABLE_ACTIONS = ["set_live", "set_draft", "set_archived", "set_deleted"];

function Fact({ label, value, sub }) {
  return (
    <div>
      <span className="peb-period-detail__fact-label">{label}</span>
      <span className="peb-period-detail__fact-value">{value}</span>
      {sub && <> <span className="peb-period-detail__fact-sub">{sub}</span></>}
    </div>
  );
}

/**
 * The selected period in full: who it is, the facts that matter, where it sits
 * in its module's chain, and what cannot be done to it and why.
 */
export default function PeriodDetail({ period, periods, menuItems, onSelect, chainAnchorId, lockContext }) {
  const { pt, ps } = usePeriodStrings();

  const module = moduleOf(period);
  const source = period?.source_period || previousOf(periods, period)?.name || null;
  // "First period of the module" is only true when the module holds nothing
  // else. A period created without a takeover next to others has no source,
  // but it is not the first (LIX-1762).
  const isOnlyPeriodInModule = !(periods || [])
    .some((p) => p.id !== period?.id && isSameModule(moduleOf(p), module));

  const lineage = useMemo(() => {
    if (!period) return [];

    // Newest first: evaluation_period_start_date DESC.
    const chain = chainOf(periods, module).reverse();
    const anchorIndex = Math.max(0, chain.findIndex((p) => p.id === (chainAnchorId ?? period.id)));
    const from = Math.max(0, Math.min(anchorIndex - 3, chain.length - LANE_WINDOW));

    return chain.slice(from, from + LANE_WINDOW);
  }, [periods, period, module, chainAnchorId]);

  const locks = useMemo(
    () => groupedLocksFor(
      period,
      LOCKABLE_ACTIONS,
      (action) => ps(`period_management.action.${period?.status === PERIOD_STATUS_ARCHIVED && action === "set_live" ? "reactivate" : action}`),
      ps("period_management.lock.join_word"),
      lockContext,
    ),
    [period, ps, lockContext],
  );

  if (!period) {
    return (
      <div className="peb-period-detail">
        <p className="peb-period-detail__desc">{pt("period_management.detail.empty")}</p>
      </div>
    );
  }

  const copying = period.ready === false;

  return (
    <div className={`peb-period-detail peb-mod--${moduleToSortValue(module)}`}>
      <div className="peb-period-detail__head">
        <span
          className={`peb-period-detail__star ${isDefaultPeriod(period) ? "is-default" : ""}`}
          title={isDefaultPeriod(period) ? ps("period_management.default_period") : ""}
          aria-hidden="true"
        >
          ★
        </span>
        <div>
          <h4 className="peb-period-detail__title">
            {period.name}
            <span className="peb-module-badge">{moduleToFallback(module)}</span>
          </h4>
          {period.description && <p className="peb-period-detail__desc">{period.description}</p>}
        </div>
        <div className="peb-period-detail__head-actions">
          <ActionMenu items={menuItems} label={ps("period_management.actions_menu")} />
        </div>
      </div>

      {copying && <p className="peb-period-detail__note">{pt("period_management.detail.copying")}</p>}

      <div className="peb-period-detail__facts">
        <Fact
          label={pt("period_management.detail.status")}
          value={<span className={`peb-status-pill peb-status-pill--${copying ? "pending" : period.status}`}>
            {pt(copying ? "period_management.status.pending" : `period_management.status.${period.status}`)}
          </span>}
        />
        <Fact
          label={pt("period_management.detail.evaluation_period")}
          value={hasRange(period)
            ? `${formatDay(period.evaluation_period_start_date)} - ${formatDay(period.evaluation_period_end_date)}`
            : ps("period_management.detail.no_range")}
        />
        <Fact
          label={pt("period_management.detail.active_employees")}
          value={period.active_employee_count
            ? Number(period.active_employee_count).toLocaleString("de-DE")
            : ps("period_management.detail.no_employees")}
        />
        <Fact
          label={pt("period_management.detail.source_period")}
          value={source || ps(isOnlyPeriodInModule
            ? "period_management.detail.no_source"
            : "period_management.detail.no_source_other")}
        />
      </div>

      {lineage.length > 0 && (
        <div className="peb-period-detail__lineage">
          <span className="peb-period-detail__fact-label">
            {ps("period_management.detail.lineage", { module: moduleToFallback(module) })}
          </span>
          <div className="peb-period-detail__lineage-items">
            {lineage.map((entry) => (
              <button
                type="button"
                data-testid={`lineage-item-${entry.id}`}
                key={entry.id}
                className={[
                  "peb-period-detail__lineage-item",
                  entry.id === period.id ? "is-current" : "",
                  entry.status === PERIOD_STATUS_ARCHIVED ? "is-archived" : "",
                ].filter(Boolean).join(" ")}
                onClick={() => onSelect(entry)}
              >
                <span className="peb-period-detail__lineage-name">
                  <span className="peb-period-detail__dot" />
                  {isDefaultPeriod(entry) ? "★ " : ""}{entry.name}
                </span>
                <span className="peb-period-detail__lineage-range">
                  {formatDay(entry.evaluation_period_start_date)} - {formatDay(entry.evaluation_period_end_date)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {locks.length > 0 && (
        <div className="peb-period-detail__locks">
          <span className="peb-period-detail__fact-label">{ps("period_management.detail.locks_title")}</span>
          {locks.map((lock) => (
            <p className="peb-period-detail__lock" key={lock.reasonKey}>
              <i className="bi bi-x-lg" aria-hidden="true" />
              <span>
                <strong>{lock.what}</strong>: {pt(
                  lock.reasonKey,
                  openPeriodLimitReplacementsFor(periods, module),
                )}
              </span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
